import {
  createContext,
  createElement,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import type { ReactNode } from "react";

import {
  get_recording_os_permissions,
  isApiRequestError,
  pause_recording,
  resume_recording,
  sync_recording,
  start_recording,
  stop_recording,
} from "../api";
import { connectReconnectingSocket } from "../api/low_level";
import type {
  RecordingOsPermissionsPayload,
  RecordingSyncStatus,
  StatusPayload,
} from "../api/types";
import type { RecordingStatus } from "../components/dashboard/types";
import { frontendCapabilities } from "../globals";

type RecordingControls = {
  recordingStatus: RecordingStatus;
  recordingElapsedSeconds: number;
  recordingSyncStatus: RecordingSyncStatus;
  recordingSyncCompletionId: number;
  /** A successful upload, excluding local screenshot finalization. */
  recordingUploadCompletionId: number;
  /** Finished recordings awaiting the user's approval or deletion. */
  pendingReviewCount: number;
  /** A new session became reviewable after the initial status snapshot. */
  recordingReviewReady: { id: number; count: number } | null;
  handleStartRecording: () => Promise<void>;
  handlePauseRecording: () => Promise<void>;
  handleStopRecording: () => Promise<void>;
  handleRetryRecordingSync: () => Promise<void>;
  recordingPermissionsAvailable: boolean;
  /** A required permission is missing: recording can't start. */
  recordingPermissionsRequired: boolean;
  /** An optional permission is missing: recording works, with less detail. */
  recordingPermissionsRecommended: boolean;
  recordingPermissionsOpen: boolean;
  openRecordingPermissions: () => void;
  closeRecordingPermissions: () => void;
  handleRecordingPermissionsStatus: (status: RecordingOsPermissionsPayload) => void;
  applyRecordingStatus: (res: StatusPayload) => void;
};

const RecordingControlsContext = createContext<RecordingControls | null>(null);

const useRecordingControlsState = (): RecordingControls => {
  const [recordingStatus, setRecordingStatus] = useState<RecordingStatus>("idle");
  const [elapsedBeforeCurrentResume, setElapsedBeforeCurrentResume] = useState(0);
  const [lastResumeTimeMs, setLastResumeTimeMs] = useState<number | null>(null);
  const [clockTick, setClockTick] = useState(0);
  const [recordingSyncStatus, setRecordingSyncStatus] = useState<RecordingSyncStatus>({
    status: "idle",
  });
  const [recordingSyncCompletionId, setRecordingSyncCompletionId] = useState(0);
  const [recordingUploadCompletionId, setRecordingUploadCompletionId] = useState(0);
  const previousSyncStatus = useRef<RecordingSyncStatus["status"]>("idle");
  const [pendingReviewCount, setPendingReviewCount] = useState(0);
  const [recordingReviewReady, setRecordingReviewReady] =
    useState<RecordingControls["recordingReviewReady"]>(null);
  const previousPendingReviewCount = useRef<number | null>(null);
  const recordingSyncWasActive = useRef(false);
  const statusRevision = useRef(0);
  const [recordingPermissionsAvailable, setRecordingPermissionsAvailable] = useState(false);
  const [recordingPermissionsRequired, setRecordingPermissionsRequired] = useState(false);
  const [recordingPermissionsRecommended, setRecordingPermissionsRecommended] = useState(false);
  const [recordingPermissionsOpen, setRecordingPermissionsOpen] = useState(false);

  const applyRecordingStatus = useCallback((res: StatusPayload) => {
    if (!frontendCapabilities.localRecording) {
      return;
    }

    if (!res.ok) {
      return;
    }
    statusRevision.current += 1;
    const elapsedSeconds = Number(res.elapsed_seconds ?? 0);
    if (res.sync_status.status === "syncing" || res.sync_status.status === "finalizing") {
      recordingSyncWasActive.current = true;
    } else if (recordingSyncWasActive.current) {
      recordingSyncWasActive.current = false;
      if (res.sync_status.status === "idle") {
        setRecordingSyncCompletionId(current => current + 1);
      }
    }
    if (previousSyncStatus.current === "syncing" && res.sync_status.status === "idle") {
      setRecordingUploadCompletionId(current => current + 1);
    }
    previousSyncStatus.current = res.sync_status.status;
    setRecordingStatus(res.status);
    setRecordingSyncStatus(res.sync_status);
    if (
      previousPendingReviewCount.current !== null &&
      res.pending_review > previousPendingReviewCount.current
    ) {
      const count = res.pending_review - previousPendingReviewCount.current;
      setRecordingReviewReady(current => ({ id: (current?.id ?? 0) + 1, count }));
    }
    previousPendingReviewCount.current = res.pending_review;
    setPendingReviewCount(res.pending_review);
    setElapsedBeforeCurrentResume(elapsedSeconds);
    setLastResumeTimeMs(res.status === "recording" ? Date.now() : null);
  }, []);

  // Recording state can change outside this renderer (the desktop tray), so
  // follow the core's pushed snapshots: one on connect, then one per recording
  // or sync transition.
  useEffect(() => {
    if (!frontendCapabilities.localRecording) {
      return;
    }

    return connectReconnectingSocket<StatusPayload>("/ws/recording-status/", applyRecordingStatus);
  }, [applyRecordingStatus]);

  // The permissions modal reports every status it learns (on open, on window
  // focus, after an action), so this stays in step with the OS, not just with
  // the first check on mount.
  const handleRecordingPermissionsStatus = useCallback((status: RecordingOsPermissionsPayload) => {
    setRecordingPermissionsAvailable(status.permissions.length > 0);
    setRecordingPermissionsRequired(
      status.permissions.some(permission => permission.required && !permission.granted),
    );
    setRecordingPermissionsRecommended(
      status.permissions.some(permission => !permission.required && !permission.granted),
    );
  }, []);

  useEffect(() => {
    if (!frontendCapabilities.localRecording) {
      return;
    }

    let cancelled = false;

    get_recording_os_permissions()
      .then(permissionStatus => {
        if (!cancelled) {
          handleRecordingPermissionsStatus(permissionStatus);
        }
      })
      .catch(() => {});

    return () => {
      cancelled = true;
    };
  }, [handleRecordingPermissionsStatus]);

  useEffect(() => {
    if (!frontendCapabilities.localRecording) {
      return;
    }

    if (recordingStatus !== "recording") {
      return;
    }

    const timerId = window.setInterval(() => {
      // This idiocracy should trigger re-render also
      // on child components. This should make this function to be called again
      // and an updated context should make re-render childs
      setClockTick(prev => prev + 1);
    }, 1000);

    return () => window.clearInterval(timerId);
  }, [recordingStatus]);

  const recordingElapsedSeconds =
    recordingStatus === "recording" && lastResumeTimeMs !== null
      ? elapsedBeforeCurrentResume + Math.floor((Date.now() - lastResumeTimeMs) / 1000)
      : elapsedBeforeCurrentResume;

  void clockTick;

  const openRecordingPermissions = useCallback(() => {
    setRecordingPermissionsOpen(true);
  }, [setRecordingPermissionsOpen]);

  const closeRecordingPermissions = useCallback(() => {
    setRecordingPermissionsOpen(false);
  }, [setRecordingPermissionsOpen]);

  const handleStartRecording = async () => {
    if (!frontendCapabilities.localRecording) {
      return;
    }

    try {
      const status = await start_recording();
      setRecordingPermissionsRequired(false);
      applyRecordingStatus(status);
    } catch (error) {
      if (isApiRequestError(error) && error.status === 403) {
        setRecordingPermissionsAvailable(true);
        setRecordingPermissionsRequired(true);
        setRecordingPermissionsOpen(true);
        return;
      }
      throw error;
    }
  };

  const handlePauseRecording = async () => {
    if (!frontendCapabilities.localRecording) {
      return;
    }

    const status =
      recordingStatus === "paused" ? await resume_recording() : await pause_recording();
    applyRecordingStatus(status);
  };

  const handleStopRecording = async () => {
    if (!frontendCapabilities.localRecording) {
      return;
    }

    const revisionAtStop = statusRevision.current;
    const previousStatus = recordingStatus;
    const previousElapsedSeconds = recordingElapsedSeconds;
    const previousElapsedBeforeCurrentResume = elapsedBeforeCurrentResume;
    const previousLastResumeTimeMs = lastResumeTimeMs;
    setRecordingStatus("stopping");
    setElapsedBeforeCurrentResume(previousElapsedSeconds);
    setLastResumeTimeMs(null);
    try {
      const status = await stop_recording();
      // Stop snapshots precede asynchronous finalization. A pushed snapshot may already
      // include the newly reviewable session by the time this HTTP response arrives.
      if (statusRevision.current === revisionAtStop) applyRecordingStatus(status);
    } catch {
      if (statusRevision.current === revisionAtStop) {
        setRecordingStatus(previousStatus);
        setElapsedBeforeCurrentResume(previousElapsedBeforeCurrentResume);
        setLastResumeTimeMs(previousLastResumeTimeMs);
      }
      throw new Error("Failed to stop recording");
    }
  };

  const handleRetryRecordingSync = async () => {
    if (!frontendCapabilities.localRecording) {
      return;
    }

    const status = await sync_recording();
    applyRecordingStatus(status);
  };

  return {
    recordingStatus,
    recordingElapsedSeconds,
    recordingSyncStatus,
    recordingSyncCompletionId,
    recordingUploadCompletionId,
    pendingReviewCount,
    recordingReviewReady,
    handleStartRecording,
    handlePauseRecording,
    handleStopRecording,
    handleRetryRecordingSync,
    recordingPermissionsAvailable,
    recordingPermissionsRequired,
    recordingPermissionsRecommended,
    recordingPermissionsOpen,
    openRecordingPermissions,
    closeRecordingPermissions,
    handleRecordingPermissionsStatus,
    applyRecordingStatus,
  };
};

export const RecordingControlsProvider = ({ children }: { children: ReactNode }) => {
  const controls = useRecordingControlsState();
  return createElement(RecordingControlsContext.Provider, { value: controls }, children);
};

const useRecordingControls = () => {
  const controls = useContext(RecordingControlsContext);
  if (!controls) {
    throw new Error("useRecordingControls must be used within RecordingControlsProvider");
  }
  return controls;
};

export default useRecordingControls;
